import React, {useEffect} from "react";
import {observer} from "mobx-react-lite";
import {useSitStores, useStores} from "../../../../stores";
import {generatePath, useLocation, useNavigate, useParams} from "react-router";
import {
    Alert,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    Grid,
    List,
    ListItem,
    ListItemText,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import {KoiFacade} from "../../../../components/koi-facade/KoiFacade";
import {InitiativeFinancialForm} from "./initiativeFinancialForm";
import {sitRoutes} from "../../../routing/sitRoutes";
import {InitiativePlaningFrom} from "./InitiativePlaningFrom";
import {InitiativeSuccessStepFrom} from "./InitiativeSuccessStepFrom";
import {Check, Info, UploadFile} from "@mui/icons-material";
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import {InitiativeScopeForm} from "./initiativeScopeForm";
import {InitiativeGeneralForm} from "./initiativeGeneralForm";
import {InitiativeSkuForm} from "./initiativeSkuForm";
import {InitiativePeopleForm} from "./initiativePeopleForm";
import {InitiativeAttachmentForm} from "./initiativeAttachmentForm";
import {styled} from "@mui/material/styles";
import {InitiativeSplitForm} from "./initiativeSplitForm";
// import {InitiativeKpiForm} from "./initiativeKpiForm";
// import {InitiativeTeamForm} from "./initiativeTeamForm";

// See https://mui.com/material-ui/react-button/#file-upload
const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

export const InitiativeForm: React.FC = observer(() => {
    const {initiativeListStore, initiativeInstanceStore, attachmentStore, sitApi} = useSitStores();
    const {p} = useStores();
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();
    const initiativeId: number | 'new' = 'initiativeId' in params ? parseInt(params['initiativeId'] ?? '') || 'new' : 'new';
    const approvalParam = 'approval' in params && params['approval'] === 'approval';
    const [isLoading, setIsLoading] = React.useState(false);
    const [isUploading, setIsUploading] = React.useState(false);
    const [isLoadingApproval, setIsLoadingApproval] = React.useState(false);

    useEffect(() => {
        if (initiativeId === 'new')
            initiativeInstanceStore.setInstance({});
        else
            sitApi.getInitiative(initiativeId).then((resp) => {
                initiativeInstanceStore.setInstance(resp.data, approvalParam);
            }).catch((_reason) => {
                navigate(generatePath(sitRoutes.sit_initiatives));
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initiativeId]);

    return (
        <KoiFacade
            title={<>
                <Typography variant="h1" component="h1">
                    {initiativeId === 'new' ?
                        'Add New Initiative' : 'Initiative details'}
                    {initiativeId !== 'new' &&
                        <Chip label={'ID: ' + initiativeId.toString()} variant="outlined" className="mx-2"/>}
                    {initiativeInstanceStore.instance.approved &&
                        <Chip label={'Approved'} variant="outlined" color="success"/>}
                </Typography>
            </>}
            onBack={() => {
                // Release possible lock
                if (!initiativeInstanceStore.disabled)
                    initiativeInstanceStore.releaseLock();
                initiativeInstanceStore.setInstance({});
                navigate(generatePath(sitRoutes.sit_initiatives));
            }}
            backClass={""}
            next={initiativeId !== 'new' ? {
                type: 'component',
                node: <Tooltip
                    title={!initiativeInstanceStore.instance.locked_by ? '' : 'Locked by ' + initiativeInstanceStore.instance.locked_by.name}>
                    <span>
                        <Button
                            disabled={
                                initiativeInstanceStore.disabled &&
                                (initiativeInstanceStore.instance.locked_by != null) &&
                                (initiativeInstanceStore.instance.locked_by!.id !== initiativeInstanceStore.authStore.sitUserProfile?.id)
                            }
                            variant="outlined"
                            color="secondary"
                            className="lock-btn"
                            startIcon={initiativeInstanceStore.disabled ? <LockIcon/> : <LockOpenIcon/>}
                            onClick={() => {
                                if (initiativeInstanceStore.disabled)
                                    initiativeInstanceStore.getLock();
                                else
                                    initiativeInstanceStore.releaseLock();
                            }}>
                            {initiativeInstanceStore.disabled ? 'Locked' : 'Unlocked'}
                        </Button>
                    </span>
                </Tooltip>
            } : {
                type: 'component', node: <></>
            }}
            nextClass=""
            className="initiative-page">
            <form onSubmit={(event) => {
                event.preventDefault();
                setIsLoading(true);
                initiativeInstanceStore.createOrUpdate()?.then(() => {
                    initiativeInstanceStore.setSaveErrors(undefined);
                    initiativeInstanceStore.setInstance({});
                    initiativeListStore.reloadData();
                    navigate(generatePath(sitRoutes.sit_initiatives));
                }).catch(error => {
                    initiativeInstanceStore.setSaveErrors(error.response.data);
                }).finally(() => {
                    setIsLoading(false);
                });
            }}>
                <Grid container justifyContent="center">
                    <Grid
                        size={{
                            xs: 12,
                            md: 10,
                            lg: 8
                        }}>
                        <Grid container spacing={3}>
                            <Grid size={12}>
                                <Typography variant="h3" component="h3">General Information</Typography>
                            </Grid>
                            <Grid size={12}>
                                <InitiativeGeneralForm/>
                            </Grid>
                        </Grid>

                        <Grid container spacing={3}>
                            <Grid size={12}>
                                <Typography variant="h3" component="h3">Scope</Typography>
                            </Grid>
                            <Grid size={12}>
                                <InitiativeScopeForm/>
                            </Grid>
                        </Grid>

                        {(initiativeInstanceStore.f_c[0].instance.companies.length > 1 ||
                                initiativeInstanceStore.f_c[0].instance.categories.length > 1 ||
                                initiativeInstanceStore.f_c.length > 1) &&
                            <InitiativeSplitForm/>}

                        <Grid container spacing={3}>
                            <Grid size={12}>
                                <Typography variant="h3" component="h3">People & Roles</Typography>
                            </Grid>
                            <Grid size={12}>
                                <InitiativePeopleForm/>
                            </Grid>
                        </Grid>

                        {/*<Grid container spacing={3}>*/}
                        {/*    <Grid size={12}>*/}
                        {/*        <Typography variant="h3" component="h3">Team Members</Typography>*/}
                        {/*    </Grid>*/}
                        {/*    <Grid size={12}>*/}
                        {/*        <InitiativeTeamForm/>*/}
                        {/*    </Grid>*/}
                        {/*</Grid>*/}

                        <Grid container spacing={3}>
                            <Grid size={12}>
                                <Typography variant="h3" component="h3">Financial Information</Typography>
                            </Grid>
                            <Grid size={12}>
                                <InitiativeFinancialForm/>
                            </Grid>
                        </Grid>

                        {!initiativeInstanceStore.creating && <Grid container spacing={3}>
                            <Grid size={6}>
                                <Typography variant="h3" component="h3">
                                    Attachments&nbsp;
                                    <Tooltip
                                        title="Attach any documents, e.g., calculation of savings, presentations, etc.">
                                        <Info fontSize="small"/>
                                    </Tooltip>
                                </Typography>
                            </Grid>
                            <Grid size={6}>
                                <Grid container spacing={3} className="mt-2" justifyContent="end">
                                    <Grid>
                                        {initiativeInstanceStore.instance.id && initiativeInstanceStore.instance.id > 0 &&
                                            <Button
                                                disabled={initiativeInstanceStore.disabled || isUploading}
                                                variant="outlined"
                                                component="label"
                                                role={undefined}
                                                startIcon={<UploadFile/>}>
                                                Upload Document
                                                <VisuallyHiddenInput
                                                    type="file"
                                                    onChange={(event) => {
                                                        if (event.target.files && event.target.files.item(0) !== null) {
                                                            setIsUploading(true);
                                                            sitApi.createInitiativeAttachment(
                                                                initiativeInstanceStore.instance.id!, event.target.files.item(0)!
                                                            ).then(() => {
                                                                attachmentStore.initiativeAttachmentsRM.request({id: initiativeInstanceStore.instance.id!});
                                                                setIsUploading(false);
                                                            });
                                                        }
                                                    }}/>
                                            </Button>}
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid size={12}>
                                <InitiativeAttachmentForm/>
                            </Grid>
                        </Grid>}

                        <Grid container spacing={3}>
                            <Grid size={12}>
                                <Typography variant="h3" component="h3">Project Planing</Typography>
                            </Grid>
                            <Grid size={12}>
                                <InitiativePlaningFrom/>
                            </Grid>
                        </Grid>

                        {!p.p.sitHideSkuForm && <InitiativeSkuForm/>}

                        <Grid container spacing={3}>
                            <Grid size={12}>
                                <Typography variant="h3" component="h3">
                                    Key Milestones&nbsp;
                                    <Tooltip
                                        title="Key milestones that need to be achieved for realizing this initiative, you can capture free format checklist here">
                                        <Info fontSize="small"/>
                                    </Tooltip>
                                </Typography>
                            </Grid>
                            <Grid size={12}>
                                <InitiativeSuccessStepFrom/>
                            </Grid>
                        </Grid>

                        {/*<Grid container spacing={3}>*/}
                        {/*    <Grid size={6}>*/}
                        {/*        <Typography variant="h3" component="h3">KPI For Monitoring</Typography>*/}
                        {/*    </Grid>*/}
                        {/*    <Grid size={6}>*/}
                        {/*        <Grid container spacing={3} className="mt-2" justifyContent="end">*/}
                        {/*            <Grid>*/}
                        {/*                <Button*/}
                        {/*                    disabled={initiativeInstanceStore.disabled}*/}
                        {/*                    variant="outlined"*/}
                        {/*                    startIcon={<Add/>}*/}
                        {/*                    onClick={() => initiativeInstanceStore.kpiDialogOpen = true}>*/}
                        {/*                    New KPI for monitoring*/}
                        {/*                </Button>*/}
                        {/*            </Grid>*/}
                        {/*        </Grid>*/}
                        {/*    </Grid>*/}
                        {/*    <Grid size={12}>*/}
                        {/*        <InitiativeKpiForm/>*/}
                        {/*    </Grid>*/}
                        {/*</Grid>*/}

                        {initiativeInstanceStore.readyForApproval && <Grid container spacing={3}>
                            <Grid size={12}>
                                <FormControl fullWidth>
                                    <TextField
                                        disabled
                                        multiline minRows={3}
                                        variant="outlined"
                                        label="Approval Notes"
                                        className="mt-3"
                                        value={initiativeInstanceStore.description.instance?.approval_notes ?? ''}/>
                                </FormControl>
                            </Grid>
                        </Grid>}

                        <Grid container spacing={3} className="mt-2" justifyContent="end">
                            {!initiativeInstanceStore.disabled && !initiativeInstanceStore.readyForApproval &&
                                <Grid>
                                    <Button
                                        disabled={initiativeInstanceStore.disabled}
                                        className="mr-4"
                                        onClick={() => {
                                            // Release possible lock
                                            if (!initiativeInstanceStore.disabled)
                                                initiativeInstanceStore.releaseLock();
                                            // Clear form data
                                            initiativeInstanceStore.setInstance({});
                                            navigate(generatePath(sitRoutes.sit_initiatives));
                                        }}>
                                        Cancel
                                    </Button>

                                    <Button
                                        disabled={isLoading || initiativeInstanceStore.disabled}
                                        className="loading-button"
                                        type="submit"
                                        startIcon={isLoading ? <CircularProgress size={14}/> : <Check/>}
                                        variant="contained">
                                        {initiativeId === 'new' ? 'Add initiative' : 'Update initiative'}
                                    </Button>
                                </Grid>
                            }

                            {initiativeInstanceStore.readyForApproval &&
                                <Grid>
                                    <Button
                                        variant="contained"
                                        className="bg-danger mr-4"
                                        onClick={() => {
                                            initiativeInstanceStore.approve(false)?.then(() => {
                                                initiativeListStore.reloadApprovalData();
                                                navigate(generatePath(sitRoutes.sit_initiatives));
                                            });
                                        }}>
                                        Reject
                                    </Button>

                                    <Button
                                        variant="contained"
                                        onClick={() => {
                                            initiativeInstanceStore.approve(true)?.then(() => {
                                                initiativeListStore.reloadApprovalData();
                                                navigate(generatePath(sitRoutes.sit_initiatives));
                                            });
                                        }}>
                                        Accept
                                    </Button>
                                </Grid>
                            }
                        </Grid>

                        {initiativeInstanceStore.save_errors && (initiativeInstanceStore.save_errors!.length > 0) &&
                            <Grid container spacing={3} className="mt-2">
                                <Grid size={12}>
                                    <Alert severity="error" color="error">
                                        Please fix the following and try again
                                        <List>
                                            {initiativeInstanceStore.save_errors.map((err_item, index) =>
                                                <ListItem key={index} disablePadding>
                                                    <ListItemText primary={err_item[0]} secondary={err_item[1]}/>
                                                </ListItem>
                                            )}
                                        </List>
                                    </Alert>
                                </Grid>
                            </Grid>}

                    </Grid>
                </Grid>
                {/*Approval */}
                <Dialog
                    open={initiativeInstanceStore.approval_dialog_open} aria-labelledby="dialog-title"
                    aria-describedby="dialog-description"
                    onClose={() => {
                        setIsLoadingApproval(false);
                        initiativeInstanceStore.approvalDialogOpen = false;
                    }}>
                    <DialogTitle id="dialog-title">
                        Approval Required
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText id="dialog-description">
                            Changing initiative status to
                            <b> "DOI5 - EBIT effective" </b>
                            requires approval from
                            <b> "{initiativeInstanceStore.instance.approver?.name}" </b>.
                            <br/>
                            Are you sure you want to submit this change?
                        </DialogContentText>
                        <Grid container className="mt-2">
                            <Grid size={12}>
                                <FormControl fullWidth>
                                    <TextField
                                        multiline minRows={3}
                                        variant="outlined"
                                        label="Approval Notes"
                                        value={initiativeInstanceStore.description.instance?.approval_notes ?? ''}
                                        onChange={(event) =>
                                            initiativeInstanceStore.description.approval_notes = event.target.value
                                        }/>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => {
                            setIsLoadingApproval(false);
                            initiativeInstanceStore.approvalDialogOpen = false;
                        }}>cancel</Button>
                        <Button
                            variant="contained"
                            disabled={isLoadingApproval}
                            className="loading-button"
                            onClick={() => {
                                setIsLoadingApproval(true);
                                initiativeInstanceStore.createOrUpdate(true)?.then(() => {
                                    initiativeInstanceStore.setSaveErrors(undefined);
                                    initiativeListStore.reloadApprovalData();
                                }).catch(error => {
                                    initiativeInstanceStore.setSaveErrors(error.response.data);
                                }).finally(() => {
                                    setIsLoadingApproval(false);
                                    initiativeInstanceStore.approvalDialogOpen = false;
                                    initiativeListStore.reloadData();
                                    navigate(generatePath(sitRoutes.sit_initiatives));
                                });
                            }}>
                            Send for approval
                        </Button>
                    </DialogActions>
                </Dialog>
            </form>
        </KoiFacade>
    );
});